import { useEffect } from 'react';
import type { AssistantUITheme } from '@adsk/adp-web-analytics-sdk/lib/AssistantUI';
import cookies from 'browser-cookies';
import { getBernoulliAdp } from '@/services/analytics/adp';
import { ActionPayload, RpcError } from './types';
import {
  hasLocalMcpServers,
  handleMcpServers,
  handleMcpExecute,
  type McpExecutePayload,
} from './localMcpProxy';

const getTenantId = (): string | undefined => {
  return sessionStorage.getItem('tenantId') || undefined;
};

const getAccessToken = (): string | undefined => {
  return cookies.get('auth0.user.token') || undefined;
};

declare global {
  interface Window {
    bernoulli?: {
      sendMessageToAssistant?: (
        message: string,
        payload?: ActionPayload,
      ) => Promise<{ result?: ActionPayload; error?: RpcError }>;
      setAssistantTheme?: (theme: AssistantUITheme) => Promise<void>;
    };
  }
}

export function useAssistantMessaging() {
  const adpClient = getBernoulliAdp();

  useEffect(() => {
    if (!adpClient) return;

    window.bernoulli = window.bernoulli ?? {};
    window.bernoulli.sendMessageToAssistant = async (message, payload) => {
      console.log('[Bernoulli] sendMessageToAssistant', message, payload);
      try {
        const result = await adpClient.sendMessageToAssistant<
          ActionPayload,
          ActionPayload
        >(message, payload);
        console.log('[Bernoulli] sendMessageToAssistant response', result);
        return { result };
      } catch (error) {
        console.error('[Bernoulli] sendMessageToAssistant error', error);
        return { error: error as RpcError };
      }
    };

    window.bernoulli.setAssistantTheme = async (theme: AssistantUITheme) => {
      console.log('[Bernoulli] setAssistantTheme', theme);
      try {
        await adpClient.setAssistantTheme(theme);
      } catch (error) {
        console.error('[Bernoulli] setAssistantTheme error', error);
        throw error;
      }
    };

    return () => {
      delete window.bernoulli?.sendMessageToAssistant;
      delete window.bernoulli?.setAssistantTheme;
    };
  }, [adpClient]);

  // Register handleMessageFromAssistant listener — reply to productContext requests
  useEffect(() => {
    if (!adpClient) return;

    const getProductContext = () => ({
      tenantId: getTenantId(),
    });

    const unsubscribe = adpClient.handleMessageFromAssistant(
      async (message, payload, reply) => {
        console.log('[Bernoulli] handleMessageFromAssistant', message, payload);
        switch (message) {
          case 'productContext': {
            const context = getProductContext();
            console.log('[Bernoulli] Replying with productContext', context);
            reply(context);
            break;
          }
          case 'mcpToken': {
            const token = getAccessToken();
            console.log('[Bernoulli] Replying with mcpToken');
            reply(token);
            break;
          }
          case 'mcpServers': {
            if (hasLocalMcpServers()) {
              const servers = handleMcpServers();
              console.log(
                '[Bernoulli] Replying with local mcpServers',
                servers,
              );
              reply(servers);
            }
            break;
          }
          case 'mcpExecute': {
            if (hasLocalMcpServers()) {
              const results = await handleMcpExecute(
                payload as McpExecutePayload,
                getAccessToken(),
              );
              console.log(
                '[Bernoulli] Replying with mcpExecute results',
                results,
              );
              reply(results);
            }
            break;
          }
        }
      },
    );

    return unsubscribe;
  }, [adpClient]);
}
